import "./ContactUs.css";
import { useState } from "react";
import listCountries from "../../utils/listCountries";

const INITIAL_FORM_STATE = {
  fullName: "",
  companyName: "",
  phone: "",
  email: "",
  address: "",
  country: "",
  subject: "",
  message: "",
};

const ContactUs = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { type: 'success' | 'error', text: '' }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage(null);

    try {
      setIsSubmitting(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/proposal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setStatusMessage({
        type: "success",
        text: "Thank you for reaching out! Your message has been sent successfully.",
      });
      setFormData(INITIAL_FORM_STATE);
    } catch (error) {
      console.error(error);
      setStatusMessage({
        type: "error",
        text: "Failed to send message. Please try again or contact us directly via email.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="legal-page contact-page">
      <div className="container">
        <header className="page-header">
          <h1>Contact Us</h1>
          <p>
            Have questions about our training programs, corporate workshops, or custom learning solutions? Get in touch with our team today.
          </p>
        </header>

        <div className="contact-layout">
          {/* Contact Form */}
          <section className="form-section" aria-labelledby="form-heading">
            <div className="contact-form-card">
              <div className="form-intro">
                <span className="contact-eyebrow">Send an Inquiry</span>
                <h2 id="form-heading">How can we help?</h2>
                <p>
                  Tell us about your training needs, corporate workshop, or custom
                  learning requirements. Our team will get back to you shortly.
                </p>
              </div>

              {statusMessage && (
                <div
                  className={`form-alert form-alert-${statusMessage.type}`}
                  role="alert"
                >
                  {statusMessage.text}
                </div>
              )}

              <form className="proposal-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="fullName">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="e.g. Jane Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="companyName">
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    autoComplete="organization"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="e.g. Enterprise Solutions Ltd"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Work Email <span className="required">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@company.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+65 8091 0792"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address (Optional)</label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    autoComplete="street-address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street Address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">
                    Country <span className="required">*</span>
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select Country
                    </option>
                    {listCountries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    Your Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help your team?"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="proposal-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </section>

          {/* Contact / Map */}
          <aside className="contact-sidebar contact-location-card">
            <div className="contact-card-header">
              <span className="contact-eyebrow">Visit or Contact Us</span>

              <h2>Get in Touch</h2>

              <p>
                Connect with our team or find us in Singapore.
              </p>
            </div>

            {/* Map */}
            <div className="contact-map">
              <iframe
                title="Trainosys Singapore Location"
                src="https://www.google.com/maps?q=Singapore&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Contact Details */}
            <div className="contact-details">

              {/* Email */}
              <a
                className="contact-detail"
                href="mailto:inquiry@trainosys.sg"
              >
                <span className="contact-detail-icon" aria-hidden="true">
                  ✉
                </span>

                <span className="contact-detail-content">
                  <span className="contact-detail-label">Email</span>
                  <span className="contact-detail-value">inquiry@trainosys.sg</span>
                </span>
              </a>

              {/* WhatsApp */}
              <a
                className="contact-detail"
                href="https://wa.me/6580910792"
                target="_blank"
                rel="noreferrer"
              >
                <span className="contact-detail-icon" aria-hidden="true">
                  ☎
                </span>

                <span className="contact-detail-content">
                  <span className="contact-detail-label">WhatsApp</span>
                  <span className="contact-detail-value">+65 8091 0792</span>
                </span>
              </a>

              {/* Location */}
              <div className="contact-detail">
                <span className="contact-detail-icon" aria-hidden="true">
                  📍
                </span>

                <span className="contact-detail-content">
                  <span className="contact-detail-label">Location</span>
                  <span className="contact-detail-value">Singapore</span>
                </span>
              </div>

              {/* Sitemap */}
              <a
                className="contact-detail"
                href="/sitemap.xml"
              >
                <span className="contact-detail-icon" aria-hidden="true">
                  ☰
                </span>

                <span className="contact-detail-content">
                  <span className="contact-detail-label">Website</span>
                  <span className="contact-detail-value">Sitemap</span>
                </span>
              </a>

            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default ContactUs;
